import { randomUUID as uuid } from 'node:crypto';
import type { Language, Marketplace, PLATFORMS } from '@amzn/journeys-common';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { auiHtml, getHtmlClass, getMarketplaceClass, getMarketplaceUrl, getPlatformClass, sanitizeWidgetHtml } from './live-preview.util';
import { useContentGridStylesheetPath, usePlatformStylesheetPath } from './useCss';
import { useIsRTL } from './useIsRTL';

/**
 * Live Preview IFrame wrapper
 * - Used only by live preview
 */
export const MarsLivePreviewIFrame: FC<{
  keepProvidedStyles: boolean;
  language: Language;
  marketplace: Marketplace;
  needsAuiJs: boolean;
  platform: PLATFORMS;
  widgetHtml: string;
}> = ({ keepProvidedStyles, language, marketplace, needsAuiJs, platform, widgetHtml }): JSX.Element => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isRTL = useIsRTL(language);
  const widgetStylesheetPath = usePlatformStylesheetPath(platform, isRTL);
  const contentGridStyleSheetPath = useContentGridStylesheetPath(platform, isRTL);
  const classes = useMemo(
    () =>
      [
        getMarketplaceClass(marketplace),
        getPlatformClass(platform),
        getHtmlClass(platform),
      ].join(' '),
    [
      platform,
      marketplace,
    ],
  );

  const channelName = useMemo(() => `IFRAME_PREVIEW_CHANNEL_${uuid()}`, []);
  const { sendMessage } = useBroadcastChannel(channelName);

  const sanitizedWidgetHtml = useMemo(
    () =>
      sanitizeWidgetHtml({
        keepProvidedScripts: needsAuiJs,
        keepProvidedStyles,
        widgetHtml,
      }),
    [
      keepProvidedStyles,
      needsAuiJs,
      widgetHtml,
    ],
  );
  // Important to only use the "initialHtml" in the iframeDoc string vs including the "sanitizedWidgetHtml" variable.
  // The broadcast channel takes care of updating in a way that doesn't create a new doc each time, allowing the iframe...
  // ... to maintain scroll position
  const [initialHTML] = useState(sanitizedWidgetHtml);
  useEffect(
    () =>
      sendMessage({
        sanitizedWidgetHtml,
      }),
    [
      sanitizedWidgetHtml,
      sendMessage,
    ],
  );

  const hasScripts = useMemo(
    () => sanitizedWidgetHtml.includes('<script') || needsAuiJs,
    [
      sanitizedWidgetHtml,
      needsAuiJs,
    ],
  );

  // Use sanitizedWidgetHtml for script-heavy widgets, initialHTML for others
  const iframeContent = hasScripts ? sanitizedWidgetHtml : initialHTML;

  const iframeDoc = useMemo(
    () =>
      `
            <html dir="${isRTL ? 'rtl' : 'ltr'}" lang="en-us" class="${classes}">
                <head>
                    <title>Journeys Live Preview</title>
                    <link rel="stylesheet" href="${widgetStylesheetPath}" />
                    <link rel="stylesheet" href="${contentGridStyleSheetPath}" />
                    <link rel="stylesheet" href="/styles/remote-widget/remote-widget-fonts.css" />
                    <script src="https://cdn.jsdelivr.net/npm/morphdom@2.6.1/dist/morphdom-umd.js"></script>
                    ${needsAuiJs ? auiHtml : ''}
                </head>
                <body>
                    <span id="preview-content">${iframeContent}</span>
                    <script>
                        const previewContent = document.getElementById('preview-content');
                        const broadcastChannel = new BroadcastChannel('${channelName}');

                        // Animated Accordion requires this
                        window.csa = (eventType, config) => () => {};

                        // Elevated Story Telling Carousel widget requires this to render the widget as mobile
                        window["ontouchstart"] = () => {}

                        broadcastChannel.addEventListener('message', (event) => {
                            const html = event.data.sanitizedWidgetHtml;
                            if (!html) return;
                            const newDoc = new DOMParser().parseFromString(html, 'text/html');
                            // Pull out head tag contents to avoid nested <head> tags
                            // Some campaign types keep the provided style tag
                            const newHeadContents = newDoc?.head?.innerHTML || '';
                            // Pull out body tag contents to avoid nested <body> tags
                            // Every campaign type will use the body tag contents
                            const newBodyContents = newDoc?.body?.innerHTML || '';
                            const newSpan = '<span>' + newHeadContents + newBodyContents + '</span>';
                            // Morph current preview content span into new span
                            const newHtml = morphdom(previewContent, newSpan, { childrenOnly: true });

                            // Fixes Elevated Story Telling Carousel widget visibility issue after DOM update
                            setTimeout(() => {
                                // Fix first element with visibility:hidden
                                const hiddenElement = document.querySelector('[style*="visibility:hidden"]');
                                if (hiddenElement) {
                                    hiddenElement.style.visibility = 'visible';
                                }
                            }, 0);
                        });

                        // Handle Mars widget links after they're dynamically created
                        const observer = new MutationObserver(() => {
                            const marketplaceURLBase = '${getMarketplaceUrl(marketplace)}';
                            document.querySelectorAll('a[href^="/"]').forEach((link) => {
                                const href = link.getAttribute('href');
                                // Skip links that have already been processed
                                if (link.hasAttribute('data-amazon-url')) {
                                    return;
                                }
                                if (href && href.startsWith('/') && !href.startsWith('//')) {
                                    try {
                                        const absoluteUrl = new URL(href, marketplaceURLBase);
                                        // Store the Amazon URL and completely remove href to prevent any navigation
                                        link.setAttribute('data-amazon-url', absoluteUrl.href);
                                        link.removeAttribute('href'); // Remove href entirely
                                        link.setAttribute('target', '_blank');
                                        link.style.cursor = 'pointer';
                                        
                                        // Add multiple event listeners to ensure we catch all possible events
                                        const handleLinkClick = function(e) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            e.stopImmediatePropagation();
                                            
                                            const amazonUrl = this.getAttribute('data-amazon-url');
                                            if (amazonUrl) {
                                                // Use postMessage to communicate with parent window instead of window.open
                                                // This prevents iframe navigation issues
                                                window.parent.postMessage({
                                                    type: 'OPEN_EXTERNAL_URL',
                                                    url: amazonUrl
                                                }, '*');
                                            }
                                            return false;
                                        };
                                        
                                        link.addEventListener('click', handleLinkClick, true); // Use capture phase
                                        link.addEventListener('mousedown', function(e) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        });
                                    } catch (e) {
                                        console.error('Error converting relative URL:', e);
                                    }
                                }
                            });
                        });

                        observer.observe(document.body, {
                            childList: true,
                            subtree: true
                        });


                    </script>
                </body>
            </html>
        `,
    [
      channelName,
      classes,
      contentGridStyleSheetPath,
      iframeContent,
      marketplace,
      isRTL,
      needsAuiJs,
      widgetStylesheetPath,
    ],
  );

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let handleClick: (event: MouseEvent) => void;
    const handleAddClickListener = () => {
      const iframeDocument = iframe?.contentDocument;
      if (!iframeDocument) return;
      iframeDocument.removeEventListener('click', handleClick);
      handleClick = (event: MouseEvent) => {
        let target = event.target as HTMLElement | null;

        // Traverse up the DOM tree to find the nearest <a> element
        while (target && target.tagName !== 'A') {
          target = target.parentElement;
        }

        if (target) {
          const anchor = target as HTMLAnchorElement;

          if (anchor.hash) {
            const id = anchor.hash.substring(1);
            const targetElement = iframeDocument.getElementById(id);

            if (targetElement) {
              event.preventDefault();
              targetElement.scrollIntoView({
                behavior: 'smooth',
              });
            }
            return;
          }
          if (anchor.href) {
            event.preventDefault();
            window.open(anchor.href, '_blank');
            return;
          }
        }
      };

      iframeDocument.addEventListener('click', handleClick);
    };

    const onLoad = () => {
      handleAddClickListener();
    };

    iframe.addEventListener('load', onLoad);

    return () => {
      iframe.removeEventListener('load', onLoad);

      // Clean up the click event listener if the iframe is reloaded or unmounted
      const iframeDocument = iframe.contentDocument;
      if (iframeDocument) {
        iframeDocument.removeEventListener('click', handleClick);
      }
    };
  }, []);

  // Listen for postMessage from iframe to open external URLs
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Accept messages from any origin for iframe communication
      if (event.data && event.data.type === 'OPEN_EXTERNAL_URL' && event.data.url) {
        window.open(event.data.url, '_blank');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return <iframe data-cy="livePreviewIframe" id="remoteWidgetPreview" ref={iframeRef} srcDoc={iframeDoc} title="Widget preview" />;
};
